import Vue from 'vue'
import VueRouter from 'vue-router'
import { RawLocation, Route, RouteConfig } from 'vue-router'
import { setToken } from '@/services/axios'

Vue.use(VueRouter)

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = (function push(
    this: VueRouter, location: RawLocation, onResolve?: (route: Route) => void, onReject?: (error: Error) => void
) {
    if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);

    return (originalPush.call(this, location) as unknown as Promise<Route>).catch((error: Error) => {
        return error.name === 'NavigationDuplicated' ? this.currentRoute : Promise.reject(error);
    });
}) as typeof VueRouter.prototype.push;

// Route Components
const Home = () => import('@/views/home/index.vue')
const Result = () => import('@/views/result/result.vue')
const Racing = () => import('@/views/racing/index.vue')
const RacingCountryPage = () => import('@/components/racing/RacingCountryPage.vue')
const ResultDetail = () => import('@/components/result/result-detail.vue')
const History = () => import('@/views/history/index.vue')
const HistoryDetail = () => import('@/views/history/detail.vue')
const Error404 = () => import('@/views/404/Error404.vue')
const SearchUrl = () => import('@/views/searchUrl/index.vue')

// Route Configurations
const routes: Array<RouteConfig> = [
    // Public Routes
    {
        path: '/',
        name: 'next-up',
        meta: { showLiveStream: true },
        component: Home
    },
    {
        path: '/result',
        name: 'result',
        component: Result
    },
    {
        path: '/result/:id',
        name: 'result-detail',
        component: ResultDetail
    },
    {
        path: '/racing',
        name: 'racing',
        component: Racing
    },
    {
        path: '/racing/:id',
        name: 'racing-detail',
        meta: { showLiveStream: true },
        component: RacingCountryPage,
        props: (route) => ({
            countryId: route.params.id,
            venueId: route.query['venue_id'],
            raceExternalId: route.query['race_external_id']
        })
    },
    {
        path: '/history',
        name: 'history',
        component: History
    },
    {
        path: '/history/detail',
        name: 'history-detail',
        component: HistoryDetail,
        meta: { 
            layout: 'blank'
        },
        props: (route) => ({
            transactionId: route.query['transaction_id'],
            startDate: route.query['start_date'],
            endDate: route.query['end_date'],
            status: route.query['status']
        })
    },
    {
        path: '/search',
        name: 'search',
        component: SearchUrl,
        props: (route) => ({
            account: route.query['account'],
            countryId: route.query['country_id'],
            venueId: route.query['venue_id'],
            raceExternalId: route.query['race_external_id']
        })
    },
    // 404 Route
    {
        path: '/*',
        name: 'NotFound',
        component: Error404
    }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
})

// Navigation Guards
router.beforeEach((to, from, next) => {
    // Handle token
    const tokenRedirect = new URLSearchParams(window.location.search).get('token')
    const token = tokenRedirect
    if (tokenRedirect) setToken(tokenRedirect)

    // Handle language
    const lang = to.query?.lang
    if (lang) localStorage.setItem('lang', lang as string)

    // Scroll to top
    window.scrollTo({ top: 0 })

    // Check authentication
    const hasMeta = (name: string): boolean => to.matched.some(record => record.meta[name])
    if (hasMeta('authenticated') && !token) {
        next({ name: 'Login' })
    } else if (hasMeta('guest') && token) {
        next({ name: 'Home' })
    } else {
        next()
    }
})

export default router
